import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { instance } from 'services/api';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { CURRENCY, usePaymentCurrency } from './PaymentCurrencyProvider';
dayjs.extend(utc);
dayjs.extend(timezone);

const DolarContext = createContext();

export const useDolar = () => useContext(DolarContext);

const ARG_TZ = 'America/Argentina/Buenos_Aires';
const API_URL = '/api/get_dolar_amount';
const HOURS_LIMIT = 3;

function isUpdateNeeded(lastUpdated) {
  if (!lastUpdated) return true;
  const now = dayjs().tz(ARG_TZ);
  const last = dayjs(lastUpdated).tz(ARG_TZ);
  // Si es otro día o pasaron más de 12 horas
  if (now.format('YYYY-MM-DD') !== last.format('YYYY-MM-DD')) return true;
  if (now.diff(last, 'hour') >= HOURS_LIMIT) return true;
  return false;
}

export const DolarProvider = ({ children }) => {
  const [dolarData, setDolarData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDolar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await instance.get(API_URL);
      if (response.status === 200 && response.data?.body) {
        const dataFormatted = {
          last_updated: response.data.body.last_updated,
          dolar_amount: response.data.body.dolar_amount,
        };
        setDolarData(dataFormatted);
        localStorage.setItem('dolarData', JSON.stringify(dataFormatted));
      } else {
        setError('Error al obtener el valor del dólar');
      }
    } catch (err) {
      setError('Error al obtener el valor del dólar');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;
    const checkAndFetch = () => {
      const local = localStorage.getItem('dolarData');
      let parsed = null;
      try {
        parsed = local ? JSON.parse(local) : null;
      } catch {
        parsed = null;
      }
      if (
        parsed === null ||
        (!!parsed?.last_updated && isUpdateNeeded(parsed.last_updated))
      ) {
        fetchDolar();
      } else {
        setDolarData(parsed);
        setLoading(false);
      }
    };
    checkAndFetch();
    const interval = setInterval(
      () => {
        checkAndFetch();
      },
      60 * 60 * 1000,
    ); // Cada hora
    return () => {
      clearInterval(interval);
      isMounted = false;
    };
  }, [fetchDolar]);

  return (
    <DolarContext.Provider
      value={{ dolarData, loading, error, refresh: fetchDolar }}
    >
      {children}
    </DolarContext.Provider>
  );
};

export const changeMoney = (price) => {
  const { currency } = usePaymentCurrency();
  const { dolarData } = useDolar();
  if (currency === CURRENCY.ARS) {
    return price;
  } else if (currency === CURRENCY.USD) {
    if (dolarData?.dolar_amount)
      return Math.ceil(price / dolarData.dolar_amount);
    return price / 800;
  }
};
